/** Pet Taxi price calculator (mirrors pet_taxi.py). */

const EUR_TO_LOCAL = {
  EUR: 1,
  PLN: 4.3,
  RON: 4.97,
  CHF: 0.94,
  GBP: 0.85,
  DKK: 7.45,
  HUF: 395,
  SEK: 11.5,
  NOK: 11.8,
};

const COUNTRY_GEO = {
  DE: { countrycodes: 'de', countryLabel: 'Deutschland' },
  PL: { countrycodes: 'pl', countryLabel: 'Polska' },
  RO: { countrycodes: 'ro', countryLabel: 'România' },
  PT: { countrycodes: 'pt', countryLabel: 'Portugal' },
  FI: { countrycodes: 'fi', countryLabel: 'Suomi' },
  CH: { countrycodes: 'ch', countryLabel: 'Schweiz' },
  GB: { countrycodes: 'gb', countryLabel: 'United Kingdom' },
  IT: { countrycodes: 'it', countryLabel: 'Italia' },
  FR: { countrycodes: 'fr', countryLabel: 'France' },
  NO: { countrycodes: 'no', countryLabel: 'Norge' },
  SE: { countrycodes: 'se', countryLabel: 'Sverige' },
  DK: { countrycodes: 'dk', countryLabel: 'Danmark' },
  AT: { countrycodes: 'at', countryLabel: 'Österreich' },
  HU: { countrycodes: 'hu', countryLabel: 'Magyarország' },
};

const PRIMARY_BY_COUNTRY = {
  DE: 'EUR', PL: 'PLN', RO: 'RON', PT: 'EUR', FI: 'EUR', CH: 'CHF', GB: 'GBP',
  IT: 'EUR', FR: 'EUR', NO: 'NOK', SE: 'SEK', DK: 'DKK', AT: 'EUR', HU: 'HUF',
};

function currencyForCountry(countryCode) {
  return PRIMARY_BY_COUNTRY[countryCode?.toUpperCase()] || 'EUR';
}

function convertFromEur(amountEur, currency) {
  const rate = EUR_TO_LOCAL[currency] ?? 1;
  return Math.round(Number(amountEur) * rate);
}

export function formatDriveTime(minutes, countryCode = 'DE') {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const cc = countryCode?.toUpperCase();
  if (cc === 'PL') {
    if (h && m) return `${h} godz. ${m} min`;
    if (h) return `${h} godz.`;
    return `${m} min`;
  }
  if (cc === 'RO') {
    if (h && m) return `${h} ore ${m} min`;
    if (h) return `${h} ore`;
    return `${m} min`;
  }
  if (cc === 'PT') {
    if (h && m) return `${h} h ${m} min`;
    if (h) return `${h} h`;
    return `${m} min`;
  }
  if (cc === 'FI') {
    if (h && m) return `${h} t ${m} min`;
    if (h) return `${h} t`;
    return `${m} min`;
  }
  if (cc === 'GB') {
    if (h && m) return `${h} hr ${m} min`;
    if (h) return `${h} hr`;
    return `${m} min`;
  }
  if (cc === 'FR' || cc === 'IT' || cc === 'PT') {
    if (h && m) return `${h} h ${m} min`;
    if (h) return `${h} h`;
    return `${m} min`;
  }
  if (cc === 'NO' || cc === 'SE' || cc === 'DK') {
    if (h && m) return `${h} t ${m} min`;
    if (h) return `${h} t`;
    return `${m} min`;
  }
  if (cc === 'HU') {
    if (h && m) return `${h} ó ${m} perc`;
    if (h) return `${h} ó`;
    return `${m} perc`;
  }
  if (h && m) return `${h} Std. ${m} Min.`;
  if (h) return `${h} Std.`;
  return `${m} Min.`;
}

export function calculatePetTaxiPrice(distanceKm, weightKg = 5, { largeSize = false, specialCare = 0, countryCode = 'DE' } = {}) {
  const baseEur = 45;
  const distanceFeeEur = Math.round(distanceKm * 0.28);
  const weightFeeEur = weightKg > 5 ? Math.round((weightKg - 5) * 17.6) : 0;
  const sizeFeeEur = largeSize ? 25 : 0;
  const careFeeEur = Number(specialCare) || 0;
  const totalEur = baseEur + distanceFeeEur + weightFeeEur + sizeFeeEur + careFeeEur;
  const currency = currencyForCountry(countryCode);
  const total = convertFromEur(totalEur, currency);
  return {
    currency,
    base_fee: convertFromEur(baseEur, currency),
    distance_km: distanceKm,
    distance_fee: convertFromEur(distanceFeeEur, currency),
    weight_kg: weightKg,
    weight_fee: convertFromEur(weightFeeEur, currency),
    size_fee: convertFromEur(sizeFeeEur, currency),
    care_fee: convertFromEur(careFeeEur, currency),
    total,
  };
}

export async function geocodeAddress(street, city, countryCode = 'DE') {
  const geo = COUNTRY_GEO[countryCode?.toUpperCase()] || COUNTRY_GEO.DE;
  const q = [street, city, geo.countryLabel].filter(Boolean).join(', ');
  if (!q.trim()) return null;
  try {
    const url = new URL('https://nominatim.openstreetmap.org/search');
    url.searchParams.set('q', q);
    url.searchParams.set('format', 'json');
    url.searchParams.set('limit', '1');
    url.searchParams.set('countrycodes', geo.countrycodes);
    const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.length) return null;
    return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
  } catch {
    return null;
  }
}

export async function fetchRouteKm(fromStreet, fromCity, toStreet, toCity, countryCode = 'DE') {
  const a = await geocodeAddress(fromStreet, fromCity, countryCode);
  const b = await geocodeAddress(toStreet, toCity, countryCode);
  if (!a || !b) return { km: 400, minutes: 240, coords: null };
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${a.lon},${a.lat};${b.lon},${b.lat}?overview=full&geometries=geojson`;
    const res = await fetch(url);
    const j = await res.json();
    if (j.code === 'Ok' && j.routes?.length) {
      const route = j.routes[0];
      return {
        km: Math.round((route.distance / 1000) * 10) / 10,
        minutes: Math.max(1, Math.round(route.duration / 60)),
        coords: route.geometry?.coordinates || null,
        from: a,
        to: b,
      };
    }
  } catch {
    /* fallback below */
  }
  const km = haversineKm(a.lat, a.lon, b.lat, b.lon) * 1.25;
  return {
    km: Math.round(km * 10) / 10,
    minutes: Math.max(30, Math.round((km / 100) * 60)),
    coords: [[a.lon, a.lat], [b.lon, b.lat]],
    from: a,
    to: b,
  };
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const r = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(a));
}

export function parsePageMeta(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}
